import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useActiveCareerFocus } from '../hooks/useCareerFocus';
import { useDarkMode } from '../context/DarkModeContext';
import OutcomeWizard from '../components/InternshipDashboard/Results/OutcomeWizard';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const RecordInternshipResults: React.FC = () => {
  const { focusId } = useParams<{ focusId: string }>();
  const navigate = useNavigate();
  const { darkMode } = useDarkMode();
  const { focus, loading, error } = useActiveCareerFocus('internship', focusId);
  
  const bgColor = darkMode ? 'bg-gray-900' : 'bg-gray-50';
  const cardBg = darkMode ? 'bg-gray-800' : 'bg-white';
  const textColor = darkMode ? 'text-gray-100' : 'text-gray-900';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';
  
  if (loading) {
    return (
      <div className={`min-h-screen ${bgColor} flex items-center justify-center`}>
        <div className={`animate-pulse ${textColor}`}>Loading...</div>
      </div>
    );
  }
  
  if (error || !focus) {
    return (
      <div className={`min-h-screen ${bgColor} flex items-center justify-center`}>
        <div className={`text-center ${textColor}`}>
          <p className="mb-4">Unable to load internship details</p>
          <button
            onClick={() => navigate(-1)}
            className="text-blue-500 hover:text-blue-600"
          >
            Go back
          </button>
        </div>
      </div>
    );
  }
  
  const handleComplete = () => {
    navigate(`/focus/internship/${focusId}?tab=results`);
  };
  
  const handleCancel = () => {
    navigate(`/focus/internship/${focusId}`);
  };
  
  return (
    <div className={`min-h-screen ${bgColor}`}>
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className={`flex items-center gap-2 text-sm ${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-700'}`}
          >
            <ArrowLeftIcon className="w-4 h-4" />
            Back to Dashboard
          </button>
          <h1 className={`text-3xl font-bold mt-4 ${textColor}`}>
            Record Your Internship Results
          </h1>
          <p className={`mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            {focus.company} - {focus.role}
          </p>
        </div>
        
        {/* Wizard Container */}
        <div className={`${cardBg} rounded-lg shadow-lg border ${borderColor}`}>
          <OutcomeWizard
            focus={focus}
            onComplete={handleComplete}
            onCancel={handleCancel}
          />
        </div>
      </div>
    </div>
  );
};

export default RecordInternshipResults;